import { useCallback, useEffect, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import type { ObjectLikeValidationResult } from 'utils/validation/validate-schema/validate-schema.types';

/**
 * Result type for the useAsyncParseJSONAndValidate hook
 *
 * @typedef {Object} UseAsyncParseJSONAndValidateResult
 * @property {boolean} isValid - Whether the parsed JSON is valid according to the validation function
 * @property {string | undefined} validationError - Error message from validation if applicable
 * @property {ObjectLikeValidationResult} validationResult - The result object from validation
 * @property {boolean} isValidating - Whether validation is currently in progress
 * @property {Record<string, unknown> | null} parsedData - The successfully parsed JSON data or null
 * @property {string} [parseError] - Error message from JSON parsing if applicable
 */
type UseAsyncParseJSONAndValidateResult = {
  isValid: boolean;
  validationError: string | undefined;
  validationResult: ObjectLikeValidationResult;
  isValidating: boolean;
  parsedData: Record<string, unknown> | null;
  parseError?: string;
};

/**
 * Props for the useAsyncParseJSONAndValidate hook
 *
 * @typedef {Object} UseAsyncParseJSONAndValidateProps
 * @property {string} jsonString - The JSON string to parse and validate
 * @property {Function} validationFn - Function to validate the parsed JSON data
 * @property {Function} validationFn.params - The parsed JSON object to validate
 * @property {Promise<ObjectLikeValidationResult> | ObjectLikeValidationResult} validationFn.returns - Validation result
 */
type UseAsyncParseJSONAndValidateProps = {
  jsonString: string;
  validationFn: (params: unknown) => Promise<ObjectLikeValidationResult> | ObjectLikeValidationResult;
};

/**
 * A hook that asynchronously parses a JSON string and validates it using the provided validation function.
 * It handles both synchronous and asynchronous validation functions, provides parsing error information,
 * and includes debounced processing to prevent excessive validations during rapid changes.
 *
 * @param {UseAsyncParseJSONAndValidateProps} props - The hook properties
 * @param {string} props.jsonString - The JSON string to parse and validate
 * @param {Function} props.validationFn - Function to validate the parsed JSON data
 * @returns {UseAsyncParseJSONAndValidateResult} Result object containing validation state and data
 */
export const useAsyncParseJSONAndValidate = ({
  jsonString,
  validationFn,
}: UseAsyncParseJSONAndValidateProps): UseAsyncParseJSONAndValidateResult => {
  const [isValid, setIsValid] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>();
  const [validationResult, setValidationResult] = useState<ObjectLikeValidationResult | undefined>();
  const [isValidating, setIsValidating] = useState(false);
  const [parsedData, setParsedData] = useState<Record<string, unknown> | null>(null);
  const [parseError, setParseError] = useState<string | undefined>();

  /**
   * Core function to parse JSON and validate it
   *
   * @param {string} rawString - The raw JSON string to parse and validate
   * @returns {Promise<void>} - Promise that resolves when parsing and validation are complete
   */
  const parseAndValidate = useCallback(
    async (rawString: string) => {
      if (!rawString.trim()) {
        setIsValid(false);
        setValidationError(undefined);
        setValidationResult(undefined);
        setParsedData(null);
        setParseError(undefined);
        setIsValidating(false);
        return;
      }

      setIsValidating(true);

      try {
        // Parse JSON
        const parsed = JSON.parse(rawString);
        setParsedData(parsed);
        setParseError(undefined);

        // Validate parsed data (handle both sync and async)
        try {
          const result = await validationFn(parsed);
          setValidationResult(result);
          setValidationError(undefined);
          setIsValid(result === undefined);
        } catch (validationErr) {
          console.error('validationErr', validationErr);
          // Validation function threw an error
          // Zod schema validators do not throw errors, so something else errored out
          setValidationResult(undefined);
          setValidationError(validationErr instanceof Error ? validationErr.message : 'Validation function failed');
          setIsValid(false);
        }
      } catch (jsonParseError) {
        console.error('jsonParseError', jsonParseError);
        // JSON parse error
        if (jsonParseError instanceof SyntaxError) {
          setParseError(`Invalid JSON: ${jsonParseError.message}`);
          setIsValid(false);
          setValidationResult(undefined);
          setValidationError(undefined);
          setParsedData(null);
        } else {
          // Unexpected error
          setParseError(undefined);
          setIsValid(false);
          setValidationResult(undefined);
          setValidationError('Unexpected error occurred');
          setParsedData(null);
        }
      } finally {
        setIsValidating(false);
      }
    },
    [
      validationFn,
    ],
  );

  const debouncedParseAndValidate = useDebounceCallback(parseAndValidate, 300);

  useEffect(() => {
    setIsValidating(true);
    debouncedParseAndValidate(jsonString);
  }, [
    jsonString,
    debouncedParseAndValidate,
  ]);

  return {
    isValid,
    isValidating,
    parsedData,
    parseError,
    validationError,
    validationResult,
  };
};
